import { FormEvent, useEffect, useMemo, useState } from 'react';
import { supabase } from './lib/supabase';
import type { LaunchOffer } from './types';

type Props = { offer: LaunchOffer | null; reload: () => Promise<void> };

const blank = {
  name: 'RAH 01 · Cohort 01',
  duration_hours: 24,
  domestic_amount: '',
  domestic_currency: 'IRR',
  domestic_payment_instructions: '',
  international_amount: '',
  international_currency: 'USD',
  international_payment_instructions: '',
  access_bot_username: '',
  purchase_intro: "Let's go.\nبرای ادامه ثبت‌نام، روش پرداختت رو انتخاب کن.",
};

export default function Launch({ offer, reload }: Props) {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState('');
  const [confirmation, setConfirmation] = useState('');
  useEffect(() => {
    if (!offer) return;
    setForm({
      name: offer.name,
      duration_hours: offer.duration_hours,
      domestic_amount: offer.domestic_amount?.toString() ?? '',
      domestic_currency: offer.domestic_currency,
      domestic_payment_instructions: offer.domestic_payment_instructions ?? '',
      international_amount: offer.international_amount?.toString() ?? '',
      international_currency: offer.international_currency,
      international_payment_instructions: offer.international_payment_instructions ?? '',
      access_bot_username: offer.access_bot_username ?? '',
      purchase_intro: offer.purchase_intro,
    });
  }, [offer]);

  const ready = useMemo(() => Boolean(
    form.domestic_amount && form.domestic_payment_instructions.trim()
    && form.international_amount && form.international_payment_instructions.trim()
    && form.access_bot_username.trim(),
  ), [form]);

  function field<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(current => ({ ...current, [key]: value }));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!offer) return;
    setBusy('save'); setFeedback('');
    const result = await supabase.functions.invoke('admin-launch', {
      body: {
        action: 'save_draft', offer_id: offer.id,
        offer: {
          ...form,
          duration_hours: Number(form.duration_hours),
          domestic_amount: form.domestic_amount ? Number(form.domestic_amount) : null,
          international_amount: form.international_amount ? Number(form.international_amount) : null,
        },
      },
    });
    setBusy('');
    setFeedback(result.error ? `ذخیره نشد: ${result.error.message}` : 'Draft ذخیره شد؛ هنوز برای کاربران مخفی است.');
    if (!result.error) await reload();
  }

  async function open() {
    if (!offer || confirmation !== 'OPEN_RAH_01') return;
    if (!window.confirm('این کار ثبت‌نام RAH 01 را واقعاً برای کاربران باز می‌کند. مطمئنی؟')) return;
    setBusy('open'); setFeedback('');
    const result = await supabase.functions.invoke('admin-launch', {
      body: { action: 'open', offer_id: offer.id, confirmation },
    });
    setBusy('');
    setFeedback(result.error ? `فعال نشد: ${result.error.message}` : 'Launch باز شد. Let’s go.');
    if (!result.error) { setConfirmation(''); await reload(); }
  }

  async function repairTelegramWebhook() {
    setBusy('repair'); setFeedback('');
    const result = await supabase.functions.invoke('admin-launch', {
      body: { action: 'repair_telegram_webhook' },
    });
    setBusy('');
    setFeedback(result.error
      ? `اتصال دکمه‌ها درست نشد: ${result.error.message}`
      : 'اتصال دکمه‌های تلگرام درست شد. حالا دکمه ثبت‌نام، انتخاب ایران و خارج را باز می‌کند.');
  }

  async function reopenWaitlist48h() {
    if (!offer) return;
    if (!window.confirm('ثبت‌نام RAH 01 از همین لحظه برای ۴۸ ساعت باز بماند؟')) return;
    setBusy('reopen'); setFeedback('');
    const result = await supabase.functions.invoke('admin-launch', {
      body: { action: 'reopen_waitlist_48h', offer_id: offer.id, confirmation: 'REOPEN_RAH_01_48H' },
    });
    setBusy('');
    setFeedback(result.error
      ? `ثبت‌نام باز نشد: ${result.error.message}`
      : 'ثبت‌نام از همین لحظه برای ۴۸ ساعت باز شد.');
    if (!result.error) await reload();
  }

  async function startNewLaunch24h() {
    if (!offer) return;
    if (!window.confirm('یک Launch تازه با آمار صفر از همین لحظه برای ۲۴ ساعت شروع شود؟')) return;
    setBusy('new24'); setFeedback('');
    const result = await supabase.functions.invoke('admin-launch', {
      body: { action: 'start_new_launch_24h', offer_id: offer.id, confirmation: 'START_NEW_RAH_01_24H' },
    });
    setBusy('');
    setFeedback(result.error
      ? `Launch جدید ساخته نشد: ${result.error.message}`
      : 'Launch جدید ۲۴ساعته با Funnel صفر ساخته شد.');
    if (!result.error) await reload();
  }

  if (!offer) return <section className="panel launch-empty"><h2>Shadow Launch هنوز در دیتابیس ساخته نشده.</h2><p>بعد از اجرای Migration، تنظیمات RAH 01 اینجا ظاهر می‌شود.</p></section>;

  return <section className="launch-layout">
    <form className="panel launch-form" onSubmit={save}>
      <div className="launch-title"><div><p className="eyebrow">SHADOW REGISTRATION</p><h2>ثبت‌نام RAH 01</h2></div><span className={`launch-status ${offer.status.toLowerCase()}`}>{offer.status}</span></div>
      <div className="shadow-banner">تا وقتی وضعیت DRAFT است، هیچ‌کدام از این اطلاعات در بات به مردم نمایش داده نمی‌شود.</div>
      <label>نام Offer<input value={form.name} onChange={e=>field('name',e.target.value)} disabled={offer.status!=='DRAFT'}/></label>
      <label>متن شروع خرید<textarea rows={3} value={form.purchase_intro} onChange={e=>field('purchase_intro',e.target.value)} disabled={offer.status!=='DRAFT'}/></label>
      <div className="pair"><label>مدت بازبودن (ساعت)<input type="number" min="1" max="168" value={form.duration_hours} onChange={e=>field('duration_hours',Number(e.target.value))} disabled={offer.status!=='DRAFT'}/></label><label>Access Bot username<input value={form.access_bot_username} onChange={e=>field('access_bot_username',e.target.value)} placeholder="@..." disabled={offer.status!=='DRAFT'}/></label></div>
      <fieldset><legend>پرداخت از ایران</legend><div className="pair"><label>مبلغ<input type="number" min="0" value={form.domestic_amount} onChange={e=>field('domestic_amount',e.target.value)} disabled={offer.status!=='DRAFT'}/></label><label>واحد<input value={form.domestic_currency} onChange={e=>field('domestic_currency',e.target.value)} disabled={offer.status!=='DRAFT'}/></label></div><label>اطلاعات پرداخت<textarea rows={4} value={form.domestic_payment_instructions} onChange={e=>field('domestic_payment_instructions',e.target.value)} placeholder="شماره کارت و نام صاحب حساب را بعداً خودت وارد می‌کنی." disabled={offer.status!=='DRAFT'}/></label></fieldset>
      <fieldset><legend>پرداخت از خارج ایران</legend><div className="pair"><label>مبلغ<input type="number" min="0" value={form.international_amount} onChange={e=>field('international_amount',e.target.value)} disabled={offer.status!=='DRAFT'}/></label><label>واحد<input value={form.international_currency} onChange={e=>field('international_currency',e.target.value)} disabled={offer.status!=='DRAFT'}/></label></div><label>اطلاعات پرداخت<textarea rows={4} value={form.international_payment_instructions} onChange={e=>field('international_payment_instructions',e.target.value)} placeholder="روش پرداخت بین‌المللی را بعداً خودت تأیید می‌کنی." disabled={offer.status!=='DRAFT'}/></label></fieldset>
      {feedback && <div className="notice">{feedback}</div>}
      <button className="primary" disabled={offer.status!=='DRAFT'||busy==='save'}>{busy==='save'?'در حال ذخیره…':'ذخیره Draft مخفی'}</button>
    </form>
    <div className="launch-side">
      <section className="panel phone-preview"><p className="eyebrow">PRIVATE PREVIEW</p><h2>چیزی که بعداً می‌بینند</h2><div className="bot-bubble"><strong>Sinpehouse</strong><p>{form.purchase_intro}</p></div><div className="fake-key">ثبت‌نام از ایران</div><div className="fake-key">ثبت‌نام خارج از ایران</div><div className="bot-bubble muted-bubble"><p>بعد از انتخاب روش، مبلغ و اطلاعات پرداخت نمایش داده می‌شود.</p></div><div className="fake-key wine">پرداخت کردم</div><div className="bot-bubble"><p>بعدش با یک دکمه‌ی ساده، اسم و رسیدش رو می‌فرسته تا دسترسی‌ها فعال بشه.</p></div><div className="fake-key">دریافت دسترسی →</div></section>
      <section className="panel activation-lock"><p className="eyebrow">OWNER LOCK</p><h2>{ready?'از نظر اطلاعات آماده است':'هنوز کامل نیست'}</h2><ul><li className={form.domestic_amount&&form.domestic_payment_instructions?'ok':''}>قیمت و پرداخت ایران</li><li className={form.international_amount&&form.international_payment_instructions?'ok':''}>قیمت و پرداخت خارج</li><li className={form.access_bot_username?'ok':''}>اتصال Access Bot</li></ul>{offer.status==='OPEN' ? <><p>برای هر کمپین تازه، یک Funnel مستقل با آمار صفر بساز.</p><button type="button" className="danger" onClick={startNewLaunch24h} disabled={busy==='new24'}>{busy==='new24'?'در حال ساخت…':'شروع Launch جدید ۲۴ساعته'}</button><p>اگر دکمه ثبت‌نام داخل Telegram جواب نمی‌دهد، اتصال Callback را امن تعمیر کن.</p><button type="button" className="primary" onClick={repairTelegramWebhook} disabled={busy==='repair'}>{busy==='repair'?'در حال تعمیر…':'تعمیر اتصال دکمه‌های تلگرام'}</button><button type="button" className="danger" onClick={reopenWaitlist48h} disabled={busy==='reopen'}>{busy==='reopen'?'در حال بازکردن…':'بازکردن ثبت‌نام برای ۴۸ ساعت'}</button></> : <><p>برای بازکردن واقعی، عبارت زیر را دقیق وارد کن. این بخش فعلاً منتشر نشده و هیچ کاری نمی‌کند.</p><input dir="ltr" value={confirmation} onChange={e=>setConfirmation(e.target.value)} placeholder="OPEN_RAH_01" disabled={offer.status!=='DRAFT'}/><button type="button" className="danger" onClick={open} disabled={!ready||confirmation!=='OPEN_RAH_01'||busy==='open'||offer.status!=='DRAFT'}>{busy==='open'?'در حال فعال‌سازی…':'بازکردن واقعی ثبت‌نام'}</button></>}</section>
    </div>
  </section>;
}
